import type { SitePage } from "@/types";

export const pages = [
  {
    path: "/",
    label: "Home",
    title: "MySimpleBible — Read, Listen, Remember, Pray",
    description:
      "A private, offline-first Bible companion for reading, listening, notes, bookmarks, and prayer on iPhone and Android.",
    marker: "A",
    eyebrow: "Scripture For Your Everyday",
    heading: "Your Daily Bible",
    accent: "Made Simple",
    introduction:
      "Read Scripture, listen offline, save meaningful verses, and make time for prayer. A thoughtful Bible companion for the moments that matter.",
    heroImage: "/media/ios-reading.webp",
    heroImageAlt: "MySimpleBible reading screen on iPhone",
    sections: [
      {
        label: "Reading First",
        title: "More Room For Scripture",
        body:
          "The app opens to Scripture. Controls appear when they are useful and step back when it is time to read.",
        panels: [
          { index: "01", title: "Read", body: "The complete KJV ships with the app and works without a connection." },
          { index: "02", title: "Listen", body: "Download an on-device voice and keep narration private." },
          { index: "03", title: "Remember", body: "Save notes, bookmarks, highlights, and reading position locally." },
          { index: "04", title: "Pray", body: "Organise requests, people, groups, and answered prayers." },
        ],
      },
      {
        label: "Inside The App",
        title: "A Closer Look At Your Bible",
        body:
          "Reading, notes, prayer, the calendar and settings — the screens you will actually use, on both platforms.",
        image: "/media/android-reading.webp",
        imageAlt: "MySimpleBible reading screen on Android",
      },
      {
        label: "Private By Design",
        title: "Your Faith, Your Space",
        body:
          "No account is required. Notes and prayer content are stored where you created them. Optional support, downloads, and store purchase checks send only what those actions require.",
        points: ["Nothing to sign in to", "Stored where you made it", "No advertising profile", "Delete it by deleting local data"],
        dark: true,
      },
    ],
    cta: {
      title: "Keep The Word Close",
      body: "MySimpleBible Pro keeps Scripture, listening, notes, and prayer together on your device.",
      label: "Download MySimpleBible",
      to: "/download/",
    },
  },
  {
    path: "/features/",
    label: "Features",
    title: "MySimpleBible Features",
    description:
      "Explore offline Bible reading, private notes and bookmarks, on-device listening, prayer tools, translations, and appearance controls.",
    marker: "B",
    eyebrow: "Everything Has A Clear Place",
    heading: "Everything You Need",
    accent: "Close To Scripture",
    introduction:
      "Move easily from reading to listening, notes, and prayer. The tools you need stay within reach, with Scripture at the center.",
    heroImage: "/media/ios-settings.webp",
    heroImageAlt: "MySimpleBible appearance and reading settings",
    sections: [
      {
        title: "The Whole Bible, Ready Offline",
        body:
          "The King James Version is packaged with the app. Open a book, choose a chapter, and read without downloading a page.",
        panels: [
          { index: "T", title: "Translations", body: "Download additional public-domain translations when you want them." },
          { index: "S", title: "Search", body: "Find verses and saved material from one focused search surface." },
          { index: "V", title: "Verse Actions", body: "Highlight, bookmark, note, share, or begin study beside the text." },
        ],
      },
      {
        title: "Listen Wherever Life Takes You",
        body:
          "Download narration once, then listen offline. Verse text is processed on the phone rather than sent to a speech service.",
        image: "/media/ios-reading.webp",
        imageAlt: "Reading and narration in MySimpleBible",
        dark: true,
      },
      {
        title: "Return To What Mattered",
        body:
          "Bookmarks, highlights, notes, tags, and reading position stay connected to the verse and remain searchable.",
        image: "/media/android-notes.webp",
        imageAlt: "Private notes in MySimpleBible on Android",
      },
      {
        title: "Make Every Page Your Own",
        body:
          "Choose typography, colour, spacing, navigation behavior, and notification timing. Settings are local and designed to persist.",
        points: ["Multiple reading faces", "Custom page and accent colours", "Light and dark appearance", "Adjustable text scale", "Reduced motion"],
      },
    ],
    cta: {
      title: "Use Every Feature For Three Days",
      body: "The three-day Pro trial opens the complete native experience on iPhone and Android.",
      label: "See Pro Options",
      to: "/pricing/",
    },
  },
  {
    path: "/bible-study/",
    label: "Bible Study",
    title: "Read And Study With MySimpleBible",
    description:
      "Read Scripture offline, listen on device, save notes and highlights, and open focused study tools beside a verse.",
    marker: "C",
    eyebrow: "Study Beside The Verse",
    heading: "Go Deeper",
    accent: "Stay With The Word",
    introduction:
      "Study tools open from the verse you are already reading, keeping the passage visible and the context intact.",
    heroImage: "/media/android-reading.webp",
    heroImageAlt: "Bible reading in MySimpleBible on Android",
    sections: [
      {
        title: "Made For Meaningful Reading",
        body:
          "A measured column, considered type, chapter navigation, and a quiet toolbar give Scripture more room than the interface.",
        panels: [
          { index: "01", title: "Books And Chapters", body: "Direct navigation without a nested settings maze." },
          { index: "02", title: "Verse Options", body: "Open the exact action you need from the selected verse." },
          { index: "03", title: "Reading Memory", body: "Return to the book, chapter, and verse you left." },
        ],
      },
      {
        title: "Keep The Verses That Stay With You",
        body:
          "Add a note, apply a highlight, bookmark it, attach tags, or share a clean passage image.",
        image: "/media/ios-notes.webp",
        imageAlt: "Notes connected to Scripture in MySimpleBible",
      },
      {
        title: "Ready For Your Daily Rhythm",
        body:
          "The text is offline. Narration is offline after download. Saved material is local. Pro adds deeper organization and study tools without changing the reading surface.",
      },
    ],
    cta: {
      title: "Read And Study With Pro",
      body: "Choose monthly or yearly Pro in the app, then use the complete native reader on that store account.",
      label: "Choose Pro",
      to: "/pricing/",
    },
  },
  {
    path: "/prayer/",
    label: "Prayer",
    title: "Private Prayer Lists In MySimpleBible",
    description:
      "Keep prayer requests, people, groups, reminders, sessions, and answered-prayer history privately on your device.",
    marker: "D",
    eyebrow: "A Private Place For Prayer",
    heading: "Every Prayer",
    accent: "Held With Care",
    introduction:
      "Give every request a place, connect it to a person or group, and remember what changed—without creating a cloud prayer profile.",
    heroImage: "/media/ios-pray-now.webp",
    heroImageAlt: "Pray Now timer in MySimpleBible",
    sections: [
      {
        title: "Give Every Request A Place",
        body:
          "Capture a request, attach people and tags, schedule reminders, and mark an answer without losing its history.",
        panels: [
          { index: "P", title: "People", body: "Keep requests connected to the person you are carrying." },
          { index: "G", title: "Groups", body: "Organise communities without publishing their prayer content." },
          { index: "H", title: "History", body: "Remember sessions, changes, and answered prayers." },
        ],
      },
      {
        title: "Prayer In The Shape Of Your Week",
        body:
          "Use Pray Now for a focused session, a calendar for consistency, and notifications that follow the schedule you chose.",
        image: "/media/ios-prayer-calendar.webp",
        imageAlt: "Prayer calendar in MySimpleBible",
      },
      {
        title: "Personal Prayer, Kept Personal",
        body:
          "The app never receives your prayer list, never sells an interest inferred from it, and never turns prayer consistency into a public score.",
        points: ["No cloud prayer profile", "No advertising from prayer data", "No account required", "Deleted with local app data"],
        dark: true,
      },
    ],
    cta: {
      title: "Bring One Request With You",
      body: "The prayer tools are available in the same app as Scripture.",
      label: "Get MySimpleBible",
      to: "/download/",
    },
  },
  {
    path: "/pricing/",
    label: "Pricing",
    title: "MySimpleBible Pro Pricing",
    description:
      "Choose MySimpleBible Pro monthly or yearly in the App Store or Google Play, with a three-day trial for eligible customers.",
    marker: "E",
    eyebrow: "One Complete Bible Companion",
    heading: "Your Bible, Complete",
    accent: "Your Plan, Your Choice",
    introduction:
      "Every Pro plan includes the complete KJV, translations, listening, organization, prayer tools, and every appearance control.",
    sections: [
      {
        title: "Get Started In Moments",
        body:
          "Install the app, choose a plan in its Pro screen, and confirm with the App Store or Google Play.",
        panels: [
          { index: "1", title: "Install The App", body: "Download MySimpleBible from the Apple App Store or Google Play." },
          { index: "2", title: "Choose A Plan", body: "Monthly and annual options show the localized price and billing period before confirmation." },
          { index: "3", title: "Confirm In The Store", body: "Apple or Google completes the purchase and Pro opens in the app." },
        ],
      },
      {
        title: "Simple Plans, Clear Details",
        body:
          "The trial lasts three days. Cancelling stops renewal. Your local reading and prayer data remain on device if Pro ends.",
        points: ["Three-day trial for eligible customers", "No advertising", "Store-managed billing", "Restore on the purchasing platform"],
      },
    ],
    plans: [
      {
        name: "MySimpleBible Pro Monthly",
        price: "$2.99",
        cadence: "per month",
        body: "Every feature with monthly flexibility.",
        features: ["Translations", "On-device narration", "Unlimited organisation", "All appearance controls", "Complete prayer tools"],
        href: "/download/",
        featured: true,
      },
      {
        name: "MySimpleBible Pro Yearly",
        price: "$29.99",
        cadence: "per year",
        body: "The complete app with an annual renewal.",
        features: ["Everything in MySimpleBible Pro", "Best annual rate", "Three-day trial", "Restore on iOS and Android"],
        href: "/download/",
      },
    ],
    cta: {
      title: "Make Room For The Word",
      body: "Monthly and yearly MySimpleBible Pro include the same complete Bible, study, listening, and prayer experience.",
      label: "Choose MySimpleBible Pro",
      to: "/pricing/#plans",
    },
  },
  {
    path: "/download/",
    label: "Download",
    title: "Download MySimpleBible",
    description:
      "Download MySimpleBible for iPhone and Android and begin reading the complete KJV offline.",
    marker: "F",
    eyebrow: "Two Stores, One Quiet Companion",
    heading: "Your Daily Companion",
    accent: "Starts Here",
    introduction:
      "Install the native reader, then choose Pro in the app through the App Store or Google Play. Your reading and prayer data stay on your device.",
    heroImage: "/media/ios-reading.webp",
    heroImageAlt: "MySimpleBible on iPhone",
    sections: [
      {
        title: "Apple App Store",
        body: "Native iPhone and iPad interfaces with platform text scaling, sharing, notifications, and secure local storage.",
      },
      {
        title: "Google Play",
        body: "A native Android app with the same reading, notes, prayer, translation, and Pro model.",
      },
      {
        title: "More Than A Reader. Still Simple",
        body: "The complete Bible, on-device listening, private notes, and a practical prayer list arrive in one app.",
        points: ["Complete offline text", "On-device listening", "Notes that stay put", "A real prayer list"],
      },
    ],
    cta: {
      title: "Ready When You Are",
      body: "Your space starts on your device.",
      label: "See Every Feature",
      to: "/features/",
    },
  },
  {
    path: "/about/",
    label: "About",
    title: "About MySimpleBible",
    description:
      "Learn why MySimpleBible is built as a small, private, native Bible companion.",
    marker: "G",
    eyebrow: "A Small App, Built Deliberately",
    heading: "Simple",
    accent: "By Design",
    introduction:
      "Scripture, listening, notes, and prayer each have a clear place. Thoughtful design keeps your attention on the Word and your personal reflections on your device.",
    sections: [
      { title: "Scripture Comes With You", body: "Reading never waits on a page request, because the complete translation is already on the device." },
      { title: "Your Words Stay Yours", body: "Notes, prayers and highlights are local records, not a hosted social product with an account behind it." },
      { title: "Made For Your Phone", body: "iOS and Android are separate builds that respect their own interaction models rather than one wrapped web view." },
      { title: "A Voice For The Journey", body: "On-device narration needs a downloadable model, storage management, playback controls and clear offline behaviour. That complexity stays behind one listening action." },
      { title: "A Clear Picture Of The App", body: "Explore the real screens and features, with clear details about offline use, privacy, and subscriptions." },
    ],
    cta: {
      title: "See Whether It Suits You",
      body: "Use the complete reader with your own daily rhythm.",
      label: "Download",
      to: "/download/",
    },
  },
  {
    path: "/support/",
    label: "Support",
    title: "MySimpleBible support",
    description:
      "Contact MySimpleBible support and troubleshoot reading, notes, prayer tools, downloads, or Pro restore.",
    marker: "H",
    eyebrow: "Useful support starts with useful detail",
    heading: "Tell us.",
    accent: "What happened.",
    introduction:
      "Include the device, operating system, app version, screen, action, and result. Never send a password or private prayer text.",
    sections: [
      {
        title: "Four useful checks.",
        body:
          "Confirm the system version, reopen the app, retry once on a stable connection, and make sure the device uses the store account that purchased Pro.",
        panels: [
          { index: "1", title: "Reading", body: "Reset typography or navigation from Settings." },
          { index: "2", title: "Voice", body: "Confirm the complete model download before offline playback." },
          { index: "3", title: "Pro", body: "Use Restore Purchase in the app; Apple or Google verifies the store account." },
          { index: "4", title: "Data", body: "Do not reinstall before considering local notes and prayers." },
        ],
      },
      {
        title: "Pause before you reinstall.",
        body:
          "Notes, bookmarks, highlights, prayer requests, and settings are local. Removing app data can remove them permanently.",
        dark: true,
      },
      {
        title: "Contact support.",
        body: "Email support@mysimplebible.com. A screen recording is useful when it contains no private text.",
      },
    ],
    cta: {
      title: "Not installed yet?",
      body: "Available on both platforms.",
      label: "Download",
      to: "/download/",
    },
  },
  {
    path: "/accessibility/",
    label: "Accessibility",
    title: "MySimpleBible accessibility",
    description:
      "Read how MySimpleBible supports text scaling, reading faces, colour controls, listening, keyboard access, contrast, and reduced motion.",
    marker: "I",
    eyebrow: "Made to be read however you read",
    heading: "The page",
    accent: "Should adapt.",
    introduction:
      "Accessibility is embedded in typography, colour, structure, controls, motion, and the option to listen instead of read.",
    sections: [
      {
        title: "Settings that change the reading itself.",
        body:
          "Type size, reading face, line spacing, page width, foreground, background, and accent are controlled by the reader.",
        points: ["Type you can size and choose", "Colour under your control", "Listening instead of reading", "Layout that reduces load"],
      },
      {
        title: "Built to the same standard.",
        body:
          "The Vue website uses semantic landmarks, keyboard navigation, visible focus, robust contrast, responsive reflow, and reduced-motion handling.",
      },
      {
        title: "Where this page is wrong, we want to know.",
        body:
          "Email accessibility@mysimplebible.com with the page or screen, assistive technology, device, and expected result.",
      },
    ],
    cta: {
      title: "Read it your way.",
      body: "See the complete appearance and listening feature set.",
      label: "Explore Features",
      to: "/features/",
    },
  },
  {
    path: "/privacy-policy/",
    label: "Privacy",
    title: "MySimpleBible privacy policy",
    description:
      "Read how MySimpleBible handles local reading and prayer data, support messages, Pro purchases, and website visits.",
    marker: "J",
    eyebrow: "Privacy in plain language",
    heading: "Your reading.",
    accent: "Your device.",
    introduction:
      "The app is local first, with no MySimpleBible account and no server that receives your reading or prayer data. Its downloads and store subscription checks are described directly.",
    sections: [
      { title: "1. What the app stores on your device", body: "Reading position, notes, bookmarks, highlights, prayers, people you add by hand, groups, settings, reminders, downloaded texts and voices, and the Pro licence. It lives in the app's own database and is never uploaded to us. On Android the database is included in Google's cloud backup and device transfer; on iPhone it is included in the standard device backup. Downloaded texts, audio and the narration voice are excluded from backup on both." },
      { title: "2. What Pro sends to the stores and RevenueCat", body: "Apple or Google processes the purchase. RevenueCat, our subscription-status provider, receives an anonymous app-user identifier generated by its SDK, the product and transaction information supplied by the store, subscription status and expiry, platform, and limited technical information needed to operate the service. MySimpleBible does not give RevenueCat your reading position, notes, highlights, prayers, people, groups, contacts, advertising identifier, or a MySimpleBible account because no such account exists." },
      { title: "3. Restoring a purchase", body: "Restore Purchase asks Apple or Google, through RevenueCat, whether the current store account has an active MySimpleBible Pro purchase. Restore does not ask for a purchase email inside MySimpleBible and does not upload your app content. A purchase is restored on the platform account that owns it." },
      { title: "4. Files the app downloads", body: "Extra Bible texts come from a Backblaze B2 bucket, chapter and meditation audio from get.mysimplebible.com, and the optional narration voice from the sherpa-onnx release page on github.com. These are plain downloads that carry no identifier of you, but each host necessarily sees the request and your IP address." },
      { title: "5. What the app does not do", body: "There is no account, no sign-in, no analytics, no crash-reporting SDK, no advertising identifier and no third-party tracker in either app. Reminders are scheduled on the device, so there is no push token. The app asks for no contacts, location, microphone, camera or photo permission." },
      { title: "6. Information you send to support", body: "The website support form sends the name, email address, message and MySimpleBible source label you enter to Basin, our form-delivery provider, so support can reply. Do not include passwords or private prayer text." },
      { title: "7. The website", body: "The public site does not run advertising pixels or build a behavioral profile from visits. Form delivery is used only when you choose to send a support request." },
      // `Deactivate This Device` was removed from both apps, and this sentence was the last
      // place still promising it. A privacy policy naming a control the app does not draw is
      // read literally by store review and by anyone exercising a data right, so the claim is
      // replaced by what the app actually offers rather than merely deleted.
      //
      // Why the control is gone, in the app's own words (iOS
      // `native/ios/MySimpleBible/Views/MSBSettingsScreen.swift:896-899`): "The App Store has no
      // seat to give back — the entitlement follows the Apple ID across every device — so a
      // button that appears to revoke access and does not is worse than no button." Verified
      // 2026-09-04: no view on either port draws it and no handler exists; the strings survive
      // only as unreferenced constants in the payment managers.
      { title: "8. Your choices", body: "Delete local records within the app, or remove the app to delete its local data. Manage Subscription opens the store's subscription controls, which is where a Pro subscription is cancelled. Pro follows your Apple ID or Google account rather than one installation, so the app has no control that releases it from a single device; Restore Purchase simply re-checks the store for an active purchase. Apple, Google and RevenueCat retain transaction or subscription records according to their policies and legal obligations. Contact support if you want help with a privacy request." },
    ],
    cta: {
      title: "Still not clear?",
      body: "Support can answer a specific privacy question.",
      label: "Contact Support",
      to: "/support/",
    },
  },
  {
    path: "/terms-of-service/",
    label: "Terms",
    title: "MySimpleBible terms of use",
    description:
      "Read the terms for MySimpleBible app use, Pro billing, local content, support, availability, and the website.",
    marker: "K",
    eyebrow: "The whole agreement",
    heading: "Terms",
    accent: "You can finish.",
    introduction:
      "These terms cover the app, local content you create, Pro plans, support, and the public website.",
    sections: [
      { title: "1. Accepting these terms", body: "Installing or using MySimpleBible means you agree to these terms and the current privacy policy." },
      { title: "2. Your licence to use the app", body: "You receive a personal, non-transferable license for supported devices. Do not resell, reverse engineer, or misuse the app." },
      { title: "3. The content you create", body: "You remain responsible for notes, prayers, and other local content. Deleting app data can permanently remove it." },
      { title: "4. Price, Pro, and trials", body: "Pro renews monthly or yearly through the Apple App Store or Google Play until cancelled. The store shows the localized price, billing period, trial eligibility, and confirmation terms before purchase." },
      { title: "5. Availability and changes", body: "Features and compatibility may change for security, platform support, legal compliance, or product maintenance." },
      { title: "6. Cancellations and refunds", body: "Manage or cancel a subscription through the store account that purchased it. Refund requests are decided by Apple or Google under that store's rules. Our Refund Policy explains where to request one and does not limit rights that local law gives you." },
      { title: "7. Contact", body: "Questions about these terms can be sent to legal@mysimplebible.com." },
    ],
    cta: {
      title: "That is the whole agreement.",
      body: "Return to the product itself.",
      label: "See Features",
      to: "/features/",
    },
  },
  {
    path: "/refund-policy/",
    label: "Refund Policy",
    title: "MySimpleBible refund policy",
    description:
      "Learn how cancellations, free trials, and refund requests work for MySimpleBible Pro purchases from Apple or Google.",
    marker: "L",
    eyebrow: "Store purchases, store decisions",
    heading: "Refunds.",
    accent: "Clearly routed.",
    introduction:
      "MySimpleBible Pro is purchased from Apple or Google. Those stores process the payment and decide refund requests under their own rules.",
    sections: [
      { title: "1. Cancel future renewal", body: "Use Manage Subscription in MySimpleBible or the subscription controls for the Apple or Google account that purchased Pro. Cancellation stops a future renewal; it does not normally reverse a charge that has already completed, and access continues for the paid period." },
      { title: "2. Request an Apple refund", body: "For an App Store purchase, submit the request to Apple at reportaproblem.apple.com. Apple controls the transaction and makes the eligibility decision." },
      { title: "3. Request a Google Play refund", body: "For a Google Play purchase, use Google Play order history or Google Play Help while signed in to the purchasing account. Google controls the transaction and makes the eligibility decision." },
      { title: "4. Trials and plan changes", body: "A trial converts to the selected paid plan unless it is cancelled before the store's deadline. When you change monthly and yearly plans, the store shows when the change takes effect and how any remaining value is handled before you confirm." },
      { title: "5. Your legal rights", body: "This policy does not remove any mandatory refund, cancellation, or consumer right that applies where you live. Email support@mysimplebible.com if you need help identifying the correct store route, but we cannot approve a refund on a store's behalf." },
    ],
    cta: {
      title: "Need help finding the purchase?",
      body: "Tell support which platform you used, without sending a password or private prayer text.",
      label: "Contact Support",
      to: "/support/",
    },
  },
  {
    path: "/delete-account/",
    label: "Delete Account",
    title: "Delete your MySimpleBible data",
    description:
      "How to delete MySimpleBible reading, note and prayer data from your device and manage the store records associated with Pro.",
    marker: "M",
    eyebrow: "There is no account to close",
    heading: "Delete it.",
    accent: "Without guesswork.",
    introduction:
      "MySimpleBible has no sign-in, hosted profile, or cloud prayer account to close. Your local content and a store subscription are separate: deleting either one does not delete the other.",
    sections: [
      {
        label: "What Actually Exists",
        title: "Local content, not a hosted account.",
        body:
          "Everything you write lives in the app's own database on the device in your hand. Apple or Google holds the purchase, and RevenueCat processes its subscription status; none of them receives your reading or prayer content from MySimpleBible.",
        points: ["No MySimpleBible sign-in or profile", "Your writing stays on the device", "The store holds the purchase", "Local content and billing are managed separately"],
      },
      {
        label: "Step One",
        title: "Delete All Data, in the app.",
        body:
          "Open Settings, stay on the General tab, and scroll to Delete All Data near the bottom. A confirmation appears with the Delete button held disabled for a fifteen-second countdown; confirming clears everything you have written in a single transaction.",
        panels: [
          { index: "01", title: "What goes", body: "Notes, bookmarks, highlights, prayer requests and sub-prayers, people, groups, tags, and prayer history." },
          { index: "02", title: "What stays", body: "Your settings are deliberately untouched, so reading position, appearance, reminders and the Pro licence survive it." },
          { index: "03", title: "Downloads", body: "Extra translations, chapter audio and the narration voice are files, not records. Remove them in Offline Bible Reader." },
          { index: "04", title: "Settings too", body: "Reset All Settings, the row directly below, clears what the first button keeps. It runs a ten-second countdown of its own." },
        ],
      },
      {
        label: "Step Two",
        title: "Manage the store subscription.",
        body:
          "Apple or Google processes Pro, while RevenueCat supplies subscription status to the app. Removing the app does not cancel that subscription or erase records the store must retain.",
        panels: [
          { index: "01", title: "What it holds", body: "The store holds its transaction record. RevenueCat receives an anonymous app-user identifier plus product, platform, status, plan, trial and expiry information." },
          { index: "02", title: "What it never sees", body: "No reading position, note, highlight, prayer request, person or group is sent as part of subscription handling." },
          { index: "03", title: "Cancel it", body: "Manage Subscription in the app opens the subscription controls for the current Apple or Google account." },
          { index: "04", title: "Privacy request", body: "Email support@mysimplebible.com for help with a RevenueCat-related privacy request. Contact Apple or Google directly about records held under your store account." },
        ],
      },
      // Same removal as privacy §8 above. The section is kept — the confusion it exists to
      // clear up is real — but it can no longer be built on a button that does not exist, so
      // it now separates *cancelling* from *deleting* rather than *deactivating* from
      // *deleting*. `src/app.test.ts` pins this title and was updated with it.
      {
        label: "Not The Same Thing",
        title: "Cancelling Pro deletes nothing.",
        body:
          "Pro follows your Apple ID or Google account rather than one installation, so there is no in-app control that releases it from a single device. Cancelling through Manage Subscription ends the renewal at the store; it does not delete anything you wrote, and deleting what you wrote does not cancel billing.",
        points: ["Deleting data does not cancel billing", "Cancelling billing does not delete local data", "Pro follows the store account, not the device", "Restore Purchase can check the store again"],
        dark: true,
      },
      {
        label: "Removing The App",
        title: "Uninstalling takes the device copy with it.",
        body:
          "Removing MySimpleBible deletes its database and downloads from the device. It does not cancel a subscription or erase Apple, Google, or RevenueCat records. It also cannot reach a backup already taken: on Android the database is included in Google's cloud backup and device transfer, and on iPhone in the standard device backup. Run Delete All Data before uninstalling if the next backup should carry nothing you wrote.",
      },
      {
        label: "If You Would Rather Ask",
        title: "Write once, and say which record you mean.",
        body:
          "Email support@mysimplebible.com, name the platform, and say whether your question is about local data, a RevenueCat privacy request, cancellation, or a refund route. Never send a store password. Questions about the terms themselves go to legal@mysimplebible.com.",
      },
    ],
    cta: {
      title: "Nothing left to hand over.",
      body: "Read exactly what the app keeps on the device and what it sends.",
      label: "Read The Privacy Policy",
      to: "/privacy-policy/",
    },
  },
  {
    path: "/sitemap/",
    label: "Sitemap",
    title: "MySimpleBible sitemap",
    description: "Browse every public page on the MySimpleBible website.",
    marker: "N",
    eyebrow: "Every public page",
    heading: "See",
    accent: "The whole site.",
    introduction: "A complete index for people and machines who already know what they need.",
    sections: [
      { title: "The product", body: "Start with the app or explore its two main practices.", points: ["Home", "Features", "Bible study", "Prayer"] },
      { title: "Getting it", body: "Understand price and installation.", points: ["Pricing", "Download"] },
      { title: "About and help", body: "Learn who built it or resolve a question.", points: ["About", "Support", "Accessibility"] },
      { title: "Legal and machine-readable", body: "Read Privacy, Terms, Refund policy, Delete account, robots.txt, sitemap.xml, and llms.txt.", points: ["Privacy", "Terms", "Refund policy", "Delete account"] },
    ],
    cta: {
      title: "Or skip straight to the app.",
      body: "The complete reader is available on iPhone and Android.",
      label: "Download",
      to: "/download/",
    },
  },
] as const satisfies readonly SitePage[];

export const primaryNavigation = pages.filter((page) =>
  ["/features/", "/bible-study/", "/prayer/", "/pricing/"].includes(page.path),
);

export const footerNavigation = {
  Product: pages.filter((page) => ["/features/", "/bible-study/", "/prayer/", "/pricing/", "/download/"].includes(page.path)),
  Help: pages.filter((page) => ["/support/", "/accessibility/", "/sitemap/"].includes(page.path)),
  Company: pages.filter((page) => ["/about/", "/privacy-policy/", "/terms-of-service/", "/refund-policy/", "/delete-account/"].includes(page.path)),
} as const;
